import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    ReactNode,
} from "react";
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import { indicatorsEnv } from "../utils/environment";

type DisaggregatedMetric = {
    // replace with your real type fields
    [key: string]: any;
};

type CountyIndicatorsContextType = {
    data: DisaggregatedMetric[];
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
};

const CountyIndicatorsContext = createContext<CountyIndicatorsContextType | undefined>(undefined);

type Props = {
    children: ReactNode;
};

export const CountyIndicatorsProvider = ({ children }: Props) => {
    const { metricCategories } = useContext(MetricCategoriesContext);

    const [data, setData] = useState<DisaggregatedMetric[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const hasFetchedRef = useRef(false);

    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;

    const fetchCountyIndicators = async () => {
        if (!metricCategories || metricCategories.length === 0) {
            setLoading(false);
            return;
        }

        const normalizedCategories = Array.from(
            new Map(
                metricCategories.map((category) => [category.trim().toLowerCase(), category])
            ).values()
        );

        try {
            setLoading(true);
            setError(null);

            const fetchPromises = normalizedCategories.map((category) =>
                fetch(indicatorsUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    geoDetails: [],
                    orgId: 9,
                    dashboardView: category,
                    geoEncoding: "delta",
                    geoTag: null,
                    allowDefault: true,
                    environment: indicatorsEnv,
                }),
                }).then(async (response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch data for ${category}`);
                }
                return response.json();
                })
            );

            const results = await Promise.all(fetchPromises);
            const combinedData = results.flat();

            console.log('County indicators:', combinedData);

            setData(combinedData);
            hasFetchedRef.current = true;
        } catch (err) {
            setError(err instanceof Error ? err : new Error("Unknown error"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (hasFetchedRef.current) return;
        if (!metricCategories || metricCategories.length === 0) return;

        fetchCountyIndicators();
    }, [metricCategories]);

    const value = useMemo(
        () => ({
        data,
        loading,
        error,
        refetch: fetchCountyIndicators,
        }),
        [data, loading, error]
    );

    return (
        <CountyIndicatorsContext.Provider value={value}>
        {children}
        </CountyIndicatorsContext.Provider>
    );
};

export const useCountyIndicators = () => {
    const context = useContext(CountyIndicatorsContext);

    if (!context) {
        throw new Error("useCountyIndicators must be used within CountyIndicatorsProvider");
    }

    return context;
};